/**
 * Wrapped — annual Spotify-Wrapped-style PDF of the user's Treasurescare year.
 * Backend: GET /api/wrapped/{year} returns a PDF.
 */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Download, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Wrapped() {
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(String(currentYear));
  const [downloading, setDownloading] = useState(false);
  const years = [];
  for (let y = currentYear; y >= 2024; y--) years.push(String(y));

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/wrapped/${year}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `treasurescare-wrapped-${year}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Your ${year} Wrapped is ready`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate Wrapped");
    }
    setDownloading(false);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="wrapped-page">
      <div>
        <div className="overline text-secondary flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> Annual recap
        </div>
        <h1 className="text-3xl font-display font-light mt-1">Your Treasurescare Wrapped</h1>
        <p className="mt-2 text-muted-foreground max-w-xl">
          A personal PDF that celebrates your repair year: gadgets protected, £ saved, Gems earned,
          CO₂ prevented. Share it, print it, frame it.
        </p>
      </div>

      <Card className="relative overflow-hidden bg-primary text-primary-foreground border-0">
        <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-secondary/25 blur-3xl" />
        <CardContent className="pt-10 pb-12 relative z-10 flex flex-col md:flex-row items-start md:items-end gap-6 justify-between">
          <div>
            <div className="overline text-secondary">Ready to look back?</div>
            <div className="mt-2 font-display text-5xl font-light">Your year of <span className="text-secondary italic">treasure</span></div>
            <div className="mt-2 text-primary-foreground/80 text-sm">Hi {user?.name?.split(" ")[0] || "there"} — pick a year and we'll pull it together.</div>
          </div>
          <div className="flex items-end gap-3 w-full md:w-auto">
            <div className="flex-1 md:flex-none">
              <div className="overline text-secondary mb-1">Year</div>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-full md:w-32 bg-white/10 border-white/20 text-white" data-testid="wrapped-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button
              size="lg"
              className="bg-secondary text-teal hover:bg-secondary/90"
              onClick={download}
              disabled={downloading}
              data-testid="wrapped-download-btn"
            >
              {downloading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building…</>
                : <><Download className="w-4 h-4 mr-2" /> Get my Wrapped</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Share2 className="w-4 h-4" /> What's inside</CardTitle></CardHeader>
        <CardContent>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li>• Total repairs completed this year</li>
            <li>• Estimated £ saved vs. buying new</li>
            <li>• kg of CO₂ prevented from being released</li>
            <li>• Gems earned across the year</li>
            <li>• Your membership tier as of today</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
